import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import {
  login,
  loginSuccess,
  actionNeedsAuthentication,
  loginFailed,
  actionPasswordResetRequest,
  actionPasswordResetSuccess,
  actionPasswordResetFailed,
  actionPasswordResetRequestSuccess,
  actionPasswordResetRequestFailed,
  actionPasswordResetSubmit,
  logout,
  received401Action,
  received409Action,
} from './auth.actions';
import * as actions from './auth.actions';
import * as selectors from './auth.selectors';
import { map, switchMap, mergeMap, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import { of } from 'rxjs';

import { Store } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { FeatureService } from '../../modules/customer-shared/services/feature.service';
import { TrackingService } from 'src/app/modules/shared/services/tracking.service';
import {
  authenticationErrorEvent,
  authenticationEvent,
  passwordResetRequestEvent,
  passwordResetEvent,
  redirectedToLoginPage,
  pushEnabled,
  pushDisabled,
} from 'src/app/modules/auth/tracking/auth.events';
import { selectReturnUrl } from '../router/selectors';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '../../modules/customer-shared/components/dialog/dialog.component';
import { IGlimpseUser } from '../../modules/auth/models/IGlimpseUser';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular';
import * as fromCustomerScheduling from '../customer-scheduling';
import dayjs from 'dayjs';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private tracking: TrackingService,
    private store: Store<{ router: RouterReducerState }>,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private settings: FeatureService,
    private jwt: JwtHelperService,
  ) {}

  // The auth state is rehydrated from local storage before effects start, but nothing re-sends it
  // to Mixpanel, so a reloaded session used to keep tracking without a role. Reads state.user
  // rather than selectUser on purpose: an admin impersonating a customer stays tracked as the admin.
  restoreTracking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        withLatestFrom(this.store.select(selectors.selectAuthState)),
        tap(([_, state]) => {
          if (!state.user || !state.token || this.jwt.isTokenExpired(state.token)) return;

          this.tracking.identify(state.user.username);
          this.tracking.setUserData(state.user);
          Sentry.setUser({ email: state.user.email });
        }),
      ),
    { dispatch: false },
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      tap(() => AuthService.setReportToken(null)),
      mergeMap(action =>
        this.auth.loginWithUsernamePassword(action.username, action.password).pipe(
          map(loginResponse =>
            loginSuccess({
              user: loginResponse.user,
              token: loginResponse.token,
            }),
          ),
          catchError((err: Error) => of(loginFailed({ username: action.username, error: err.message }))),
        ),
      ),
    ),
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => {
          // Clears the distinct id and the Role super property, so the next user on this
          // browser isn't tracked as the previous one.
          this.tracking.reset();
          this.router.navigate(['login']);
        }),
      ),
    { dispatch: false },
  );

  loginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginFailed),
        tap(action => {
          this.tracking.track(
            authenticationErrorEvent({
              username: action.username.toLowerCase(),
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap(action => {
          this.navigate(action.user);

          if (action.user.roles.includes('Customer'))
            this.store.dispatch(fromCustomerScheduling.loadAreaPreference({}));

          this.tracking.identify(action.user.username);
          this.tracking.setUserData(action.user);
          Sentry.setUser({ email: action.user.email });

          localStorage.setItem('glimpse_token', action.token);

          this.tracking.track(authenticationEvent({ method: 'password' }));
        }),
      ),
    { dispatch: false },
  );

  actionNeedsAuthentication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actionNeedsAuthentication),
        withLatestFrom(this.store.select(selectors.selectIsImpersonated)),
        switchMap(([action, isImpersonated]) => {
          if (isImpersonated) {
            this.store.dispatch(actions.finishImpersonation());
            return of();
          } else
            return this.router.navigate(['login'], {
              queryParams: {
                returnUrl: action.returnUrl,
              },
            });
        }),
        tap(() => {
          this.tracking.track(redirectedToLoginPage());
        }),
      ),
    { dispatch: false },
  );

  received401Action$ = createEffect(() =>
    this.actions$.pipe(
      ofType(received401Action),
      withLatestFrom(this.store.select(selectReturnUrl)),
      map(([_, url]) => actionNeedsAuthentication({ returnUrl: url })),
    ),
  );

  received409Action$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(received409Action),
        tap(_ => {
          this.dialog.open(DialogComponent, {
            data: {
              header: 'Conflict',
              text: "You cannot edit this item, as it's already in use by another auditor/publisher",
            },
          });
        }),
      ),
    { dispatch: false },
  );

  passwordResetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actionPasswordResetRequest),
      mergeMap(action =>
        this.auth.requestPasswordReset(action.email).pipe(
          map(() => actionPasswordResetRequestSuccess()),
          catchError((err: Error) => {
            console.debug(err);
            return of(actionPasswordResetRequestFailed({ error: err.message }));
          }),
        ),
      ),
    ),
  );

  passwordResetSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actionPasswordResetSubmit),
      mergeMap(action => {
        this.tracking.track(passwordResetRequestEvent({ email: action.email }));
        return this.auth.resetPassword(action.email, action.password, action.token).pipe(
          map(() => actionPasswordResetSuccess({ email: action.email })),
          catchError((err: Error) => {
            return of(actionPasswordResetFailed({ error: err.message }));
          }),
        );
      }),
    ),
  );

  passwordReset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actionPasswordResetSuccess),
        tap(action => {
          this.router.navigate(['login']);
          this.tracking.track(passwordResetEvent({ email: action.email }));
        }),
      ),
    { dispatch: false },
  );

  impersonation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.impersonationSuccess),
        tap(action => {
          localStorage.setItem('glimpse_token', action.token);
          this.navigate(action.user);
        }),
      ),
    { dispatch: false },
  );

  finishImpersonation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.finishImpersonation),
        withLatestFrom(this.store.select(selectors.selectAuthState)),
        tap(([_, state]) => {
          localStorage.setItem('glimpse_token', state.token);
          this.navigate(state.user);
        }),
      ),
    { dispatch: false },
  );

  pushEnable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.pushEnable),
      mergeMap(action =>
        this.auth.addPushSubscription(action.sub).pipe(
          map(res => actions.pushEnableSuccess()),
          tap(() => this.tracking.track(pushEnabled())),
          catchError(err => {
            this.snackbar.open(err.message, 'OK');
            return of(actions.pushEnableFail());
          }),
        ),
      ),
    ),
  );

  pushDisable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.pushDisable),
      mergeMap(action =>
        this.auth.disablePush(action.sub).pipe(
          map(res => actions.pushDisableSuccess()),
          tap(() => this.tracking.track(pushDisabled())),
          catchError(err => {
            this.snackbar.open(err.message, 'OK');
            return of(actions.pushDisableFail());
          }),
        ),
      ),
    ),
  );

  refreshProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.pushEnableSuccess, actions.pushDisableSuccess),
      map(() => actions.loadProfile()),
    ),
  );

  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProfile),
      mergeMap(() =>
        this.auth.loadProfile().pipe(
          map(res => actions.loadProfileSuccess({ user: res.data })),
          catchError(err => {
            this.snackbar.open(err.message, 'OK');
            return of(actions.loadProfileFail());
          }),
        ),
      ),
    ),
  );

  loadFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadFeatures),
      mergeMap(action =>
        this.settings.getFeatures().pipe(
          map(all => {
            let recent = all.filter(x => x.startDate > dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
            return actions.loadFeaturesSuccess({
              features:
                action.for === 'User' && action.period
                  ? all.filter(x => x.startDate > dayjs(action.period).format('YYYY-MM-DD'))
                  : action.for === 'Recent'
                    ? recent
                    : all,
              recentCount: recent.length,
            });
          }),
          catchError(error => of(actions.loadFeaturesError({ error }))),
        ),
      ),
    ),
  );

  navigate(user: IGlimpseUser) {
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      if (user.roles.includes('Administrator')) {
        this.router.navigate(['admin']);
      } else if (user.roles.includes('Customer')) {
        this.router.navigate(['audits']);
      } else if (user.roles.includes('Auditor')) {
        this.router.navigate(['auditing']);
      }
    }
  }
}
